import {
  Container, SimpleGrid, Heading,
  useDisclosure,
  HStack,
  VStack,
  Text,
  Box,
  Button,
  Stack,
  List,
  ListItem,
  Link,
} from '@chakra-ui/react';
import LoadingSpinner from '@src/components/shared/LoadingSpinner';
import EmptyState from '@src/components/shared/EmptyState';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { selectedDatabaseIdState } from '@transport-insights/uikit';
import { QUARTERS } from '@src/modules/performance/shared/constants';
import RoleGuard from '@src/components/shared/RoleGuard';
import MoreInfoDrawer from '@src/components/shared/MoreInfoDrawer';
import { useCurrentTtmEntryPeriod, useTtmList } from '../../../context/temporary-traffic-management-api-hooks';
import TtmCard from './components/TtmCard';
import TtmResultForm from './components/TtmResultForm';
import { calculateProgress } from '../shared/helpers';
import DevTools from '../shared/components/DevTools';
import TtmSiteInspectionForm from './components/TtmSiteInspectionForm';
import TtmSiteInspectionView from './components/TtmSiteInspectionView';

export default function TtmResults() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const { data, isLoading } = useTtmList();
  const { data: currentEntryPeriod, error: entryPeriodError } = useCurrentTtmEntryPeriod();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isSiteInspectionFormOpen, onOpen: onOpenSiteInspectionForm, onClose: onCloseSiteInspectionForm } = useDisclosure();
  const { isOpen: isSiteInspectionViewOpen, onOpen: onOpenSiteInspectionView, onClose: onCloseSiteInspectionView } = useDisclosure();
  const [selectedEntry, setSelectedEntry] = useState(null);
  const errorStatus = entryPeriodError?.response?.status;
  const [isTestMode, setIsTestMode] = useState(false);
  const [groupedData, setGroupedData] = useState({});

  // Create an empty entry for the current period
  const currentEntry = {
    id: null,
    quadDatabaseId: rcaId,
    quadLockYear: currentEntryPeriod?.lockYear,
    quarter: currentEntryPeriod?.quarter,
    ttmCostConfidence: null,
    ttmCost: null,
    nltpCost: null,
    unattendedSiteInspections: null,
    completedSiteInspections: null,
  };

  // Combine the current entry with the existing data
  const allEntries = [...(data || []), currentEntry];

  const dataYears = [...new Set(allEntries.map((item) => item?.quadLockYear))]
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' }));

  // Group the data by year and quarter
  // and insert the calculated progress and phase
  const handleGroupData = () => {
    const grouped = dataYears.reduce((acc, year) => {
      const yearEntries = QUARTERS.map((quarter) => {
        const entry = allEntries.find(
          (item) => item.quadLockYear === year && item.quarter === quarter.id,
        ) || {
          id: null,
          quadDatabaseId: rcaId,
          quadLockYear: year,
          quarter: quarter.id,
          ttmCostConfidence: null,
          ttmCost: null,
          nltpCost: null,
          unattendedSiteInspections: null,
          completedSiteInspections: null,
        };

        const {
          progress, phase, daysUntilEnd, daysUntilStart,
        } = calculateProgress(currentEntryPeriod?.entryStartDate, currentEntryPeriod?.entryEndDate);

        const isFutureQuarter = year > currentEntryPeriod?.lockYear || (year === currentEntryPeriod?.lockYear && quarter.id > currentEntryPeriod?.quarter);
        const isPastQuarter = year < currentEntryPeriod?.lockYear || (year === currentEntryPeriod?.lockYear && quarter.id < currentEntryPeriod?.quarter);
        const isCurrentQuarter = year === currentEntryPeriod?.lockYear && quarter.id === currentEntryPeriod?.quarter;

        const isEditable = isCurrentQuarter;

        return {
          ...entry,
          isFutureQuarter,
          isPastQuarter,
          isEditable,
          progress,
          phase,
          daysUntilEnd,
          daysUntilStart,
          entryStartDate: currentEntryPeriod?.entryStartDate,
          entryEndDate: currentEntryPeriod?.entryEndDate,
        };
      });
      acc[year] = yearEntries;
      return acc;
    }, {});
    setGroupedData(grouped);
  };

  useEffect(() => {
    handleGroupData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, currentEntryPeriod, rcaId]);

  const handleSelectEntry = (item) => {
    if (item.isEditable) {
      setSelectedEntry(item);
      onOpen();
    }
  };

  const handleCloseResultForm = () => {
    setSelectedEntry(null);
    onClose();
  };

  if ([401, 403, 500].includes(errorStatus)) {
    return (
      <Container maxW="full" flexDirection="column" display="flex" flexGrow={1}>
        <HStack mb={6} width="100%" justify="space-between">
          <Heading as="h1">Temporary Traffic Management</Heading>
        </HStack>
        <EmptyState
          title="Access Denied"
          message={(
            <>
              <Text color="gray.400">You do not have permission to view this page.</Text>
              <Text color="gray.400">Please sign in or select a different RCA.</Text>
            </>
          )}
        />
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxW="full" flexDirection="column" display="flex" flexGrow={1}>
        <HStack mb={6} width="100%" justify="space-between">
          <Heading as="h1">Temporary Traffic Management</Heading>
        </HStack>
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <>
      {selectedEntry && (
        <TtmResultForm isOpen={isOpen} onClose={() => handleCloseResultForm()} selectedEntry={selectedEntry} isTestMode={isTestMode} />
      )}
      <TtmSiteInspectionView isOpen={isSiteInspectionViewOpen} onClose={onCloseSiteInspectionView} />
      <RoleGuard allowedRoles={['TTMSiteCapture']}>
        <TtmSiteInspectionForm isOpen={isSiteInspectionFormOpen} onClose={onCloseSiteInspectionForm} />
      </RoleGuard>
      <Container maxW="full" flexDirection="column" display="flex" flexGrow={1}>
        <Stack direction={{ base: 'column', md: 'row' }} mb={6} width="100%" justify="space-between">
          <Heading as="h1">Temporary Traffic Management</Heading>
          <HStack>
            <Button onClick={onOpenSiteInspectionView} variant="ghost" size="md">View Site Inspections</Button>
            <RoleGuard allowedRoles={['TTMSiteCapture']}>
              <Button onClick={onOpenSiteInspectionForm} size="md">Add Site Inspection</Button>
            </RoleGuard>
            <MoreInfoDrawer title="More Information" size="lg">
              <Text mb={4}>
                Workers and road users need to be protected from hazards created by work sites in the road corridors. However, there is community dissatisfaction with the number of temporary work sites, the costs of inefficient and unnecessary work sites, and excessive use of traffic cones. Temporary traffic management (TTM) has become a major issue for councils and communities around the country.
              </Text>
              <Text mb={4}>
                The GPS 2024-27 sets expectations around more efficient and effective TTM. Road Efficiency Group (REG) are required to publicly report on TTM costs for all Road Controlling Authorities (RCAs) as part of NZTA’s Performance and Efficiency Plan.
              </Text>
              <Text mb={4}>
                REG has developed a wide range of reporting metrics in the Transport Insights web portal to benchmark network performance. TTM data will help the sector enhance its TTM effectiveness and ensure value for money by optimising resource allocation, reducing unnecessary expenses and improving safety.
              </Text>
              <Text mb={4}>
                REG collects this TTM information from Territorial Local Authorities (TLAs) on behalf of NZTA. TLAs are required to enter the quarter TTM results following the completion of the quarter (July-Sept in early October, Oct-Dec in early January, Jan-Mar in early April, Apr-Jun in early July).
              </Text>
              <Box mb={4} p={4} background="gray.100" borderRadius="md">
                <Text fontStyle="italic" fontWeight="bold">
                  Note:
                </Text>
                <List styleType="number" pl={4}>
                  <ListItem>You must enter values for all fields.</ListItem>
                  <ListItem>
                    Enter costs for the financial year to date, in thousands
                    <br />
                    e.g., $23M should be entered as 23000
                  </ListItem>
                  <ListItem>Enter the inspection counts for the quarter</ListItem>
                </List>
              </Box>
              <Text mb={4}>
                The TTM year to date cost percentage and redundant count information is reported quarterly to the Minister of Transport as part of the NZTA Performance and Efficiency Plan reports.
              </Text>
              <Text mb={4}>
                The year to date TTM cost percentage is presented in the Performance Dashboard for each RCA.
              </Text>
              <Text mb={4} fontWeight="bold">
                Information Sources and Relevant Links:
              </Text>
              <List styleType="disc" pl={4} mb={4}>
                <ListItem><Link isExternal href="https://www.nzta.govt.nz/partners/planning-and-investment/planning/road-efficiency-group/transport-insights-and-performance-reporting/temporary-traffic-management-reporting">NZTA Temporary traffic management reporting web site</Link></ListItem>
                <ListItem><Link isExternal href="https://www.nzta.govt.nz/assets/Road-Efficiency-Group/docs/temporary-traffic-management-quarterly-reporting-guidance-2024.pdf">Temporary traffic management reporting guidance [PDF]</Link></ListItem>
                <ListItem><Link isExternal href="https://www.nzta.govt.nz/partners/traffic-management/new-zealand-guide-to-temporary-traffic-management">New Zealand guide to temporary traffic management</Link></ListItem>
                <ListItem><Link isExternal href="/Home/HelpLink/ttm-overview">Transport Insights - TTM and Pothole reporting help page</Link></ListItem>
              </List>
            </MoreInfoDrawer>
          </HStack>
        </Stack>
        <DevTools onChangeMockDate={handleGroupData} onChangeTestMode={(v) => setIsTestMode(v)} />
        {!Object.keys(groupedData).length ? (
          <EmptyState title="No Data" message="Sorry, there is no data available for the selected network." />
        ) : (
          <VStack align="flex-start" width="100%">
            {Object.keys(groupedData).map((year) => (
              <Box key={year} width="100%">
                <Heading as="h2" size="md" py={2} px={4} background="gray.100" borderRadius="full" display="inline-block" mb={2}>{year}</Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} width="100%" py={2}>
                  {groupedData[year].sort((a, b) => a.quarter - b.quarter)
                    .map((item, indx) => (
                      <TtmCard
                        key={indx}
                        item={item}
                        onEdit={() => handleSelectEntry(item)}
                      />
                    ))}
                </SimpleGrid>
              </Box>
            ))}
          </VStack>
        )}
      </Container>
    </>
  );
}
